import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cx } from './cx.js';
/** Maps a variant to its theme-aware `--rr-state-*` token (or neutral surface). */
const VARIANT_STATE = {
    error: '--rr-state-danger',
    success: '--rr-state-success',
    warning: '--rr-state-warning',
    info: '--rr-state-info',
};
/**
 * Background per variant, written out as FULL class literals. Tailwind only
 * emits classes it can read in the source, so a class assembled from a
 * template string is emitted only if the same literal happens to appear
 * somewhere else. These maps make each class visible to the scanner.
 *
 * `TINT_BG` is the historical 15% tint over `transparent`. `OPAQUE_BG`
 * (Overnight D6) mixes the same 15% over the page surface instead, so a
 * fixed banner reads as solid and nothing behind it shows through.
 */
const TINT_BG = {
    error: 'bg-[color-mix(in_srgb,var(--rr-state-danger)_15%,transparent)]',
    success: 'bg-[color-mix(in_srgb,var(--rr-state-success)_15%,transparent)]',
    warning: 'bg-[color-mix(in_srgb,var(--rr-state-warning)_15%,transparent)]',
    info: 'bg-[color-mix(in_srgb,var(--rr-state-info)_15%,transparent)]',
};
const OPAQUE_BG = {
    error: 'bg-[color-mix(in_srgb,var(--rr-state-danger)_15%,var(--rr-bg-surface))]',
    success: 'bg-[color-mix(in_srgb,var(--rr-state-success)_15%,var(--rr-bg-surface))]',
    warning: 'bg-[color-mix(in_srgb,var(--rr-state-warning)_15%,var(--rr-bg-surface))]',
    info: 'bg-[color-mix(in_srgb,var(--rr-state-info)_15%,var(--rr-bg-surface))]',
};
/** Default ARIA role per variant. Failures announce assertively; rest politely. */
const VARIANT_ROLE = {
    error: 'alert',
    warning: 'alert',
    success: 'status',
    info: 'status',
    neutral: 'status',
};
export function RrAlert({ variant = 'info', title, children, role, layout = 'block', opaque = false, className, ...rest }) {
    const resolvedRole = role ?? VARIANT_ROLE[variant];
    // Theme-aware tone classes. `neutral` uses the standard raised surface +
    // secondary text (the "plain framed box" several admin callouts drew);
    // every other variant is a 15%-mixed tint of its --rr-state-* token with a
    // solid 1px border + matching text, mirroring OfflineBanner's treatment.
    const toneClass = variant === 'neutral'
        ? 'bg-rr-surface-raised border-rr-border-default text-rr-fg-secondary'
        : cx(opaque ? OPAQUE_BG[variant] : TINT_BG[variant], `border-[var(${VARIANT_STATE[variant]})]`, variant === 'error' && 'text-rr-state-danger', variant === 'success' && 'text-rr-state-success', variant === 'warning' && 'text-rr-state-warning', variant === 'info' && 'text-rr-state-info');
    return (_jsxs("div", { role: resolvedRole, className: cx(layout === 'inline' ? 'inline-block' : 'block', 'rounded border p-3 text-sm', toneClass, className), ...rest, children: [title != null && title !== false ? (_jsx("span", { className: "font-medium", children: title })) : null, title != null && title !== false && children != null ? ' ' : null, children] }));
}
//# sourceMappingURL=RrAlert.js.map